import { useAuth0 } from '@auth0/auth0-react';
import { FeedbackButton } from '@strata/react-buttons';
import { BlankScreen } from '@strata/react-page-templates';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { getSupplierFeatures } from 'api/referenceAPI';
import { Header } from 'components/header/Header';
import { LOGIN_REDIRECT_KEY } from 'services/constants';
import { handleError } from 'services/Error';
import { SupplierFeatures, useSupplierFeatures } from 'services/FeatureFlags';
import { AppRoutes } from 'services/routes';
import { SocketTypes, wsConnect, wsEnsureConnected } from 'services/socketHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { setSupplierFeatures } from 'store/auth/authSlice';
import { fetchAuthProfile } from 'store/auth/authThunks';
import { fetchSupplierDetails } from 'store/reference/referenceThunks';
import { registerUser } from 'store/userSupport/userSupportThunks';
import { fetchWorklistToDoTaskCount } from 'store/worklist/worklistThunks';

/** Routes that header is hidden on */
const NoHeaderRoutes: string[] = [
    AppRoutes.termsAndConditions,
    AppRoutes.termsOfUse,
    AppRoutes.healthPlanNotes,
    AppRoutes.privacyPolicy,
    AppRoutes.updatesHistory,
];

interface RequireAuthProps {
    requiredSupplierFeature?: string;
}

/**
 * Component to determine if a user is authenticated and
 * if they are allowed to visit the page they navigated to.
 *
 * If they are: they proceed to the page
 * If not: they are redirected to the login page.
 */
export const RequireAuth = ({ requiredSupplierFeature = undefined }: RequireAuthProps) => {
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Authentication
    const supplierFeatures = useSupplierFeatures();

    // Ensure npi, permissions, and features are loaded
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const authProfile = useAppSelector((state) => state.auth.profile);
    const userProfile = useAppSelector((state) => state.userSupport.userProfile);
    const userDataLoaded = !!supplierFeatures;
    useEffect(() => {
        // Fetch all NPI details if not in store
        if (isAuthenticated && selectedNpi) {
            if (!userDataLoaded) {
                (async () => {
                    try {
                        const supplierFeaturesResult =
                            (await getSupplierFeatures(selectedNpi))?.map(
                                (feature) => feature.supplier_feature_id,
                            ) ?? [];
                        dispatch(setSupplierFeatures({ supplierFeatures: supplierFeaturesResult }));

                        // Connect to sockets
                        dispatch(wsConnect({ socketType: SocketTypes.AUCTION, npi: selectedNpi }));
                        dispatch(
                            wsConnect({ socketType: SocketTypes.NOTIFICATION, npi: selectedNpi }),
                        );
                        dispatch(
                            wsEnsureConnected({
                                socketType: SocketTypes.USER_SUPPORT,
                                npi: selectedNpi,
                            }),
                        );
                        // Only need performance socket for claims data
                        if (supplierFeaturesResult?.includes(SupplierFeatures.MEDBILLING)) {
                            dispatch(
                                wsConnect({
                                    socketType: SocketTypes.PERFORMANCE,
                                    npi: selectedNpi,
                                }),
                            );
                        }

                        await dispatch(fetchAuthProfile());
                        dispatch(fetchSupplierDetails(selectedNpi));
                        dispatch(fetchWorklistToDoTaskCount());
                    } catch (err) {
                        handleError('Error loading user data.', err as Error);
                    }
                })();
            }
            if (authProfile && !userProfile) {
                dispatch(
                    registerUser({
                        details: {
                            first_name: authProfile?.firstName,
                            last_name: authProfile?.lastName,
                        },
                    }),
                );
            }
        }
    }, [
        location, // When location changes reset userDataLoaded to attempt to reload data in event of api error
        userDataLoaded,
        isAuthenticated,
        selectedNpi,
        dispatch,
        authProfile,
        userProfile,
    ]);

    if (!isAuthenticated) {
        sessionStorage.setItem(
            LOGIN_REDIRECT_KEY,
            `${location.pathname}${location.search}${location.hash}`,
        );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        loginWithRedirect();
        return <BlankScreen />;
    }

    // Redirect to home if supplier user doesn't have required feature
    if (userDataLoaded && requiredSupplierFeature && !supplierFeatures?.[requiredSupplierFeature]) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <>
            {!NoHeaderRoutes.includes(location.pathname) && <Header />}
            {!userDataLoaded ? <BlankScreen /> : <Outlet />}
            <FeedbackButton typeformID="YRPPMZcL" hideButton={!isAuthenticated} />
        </>
    );
};
